import { Card } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppUrlEnum } from '@enums/app.enum';

interface LegalDocument {
  external?: boolean;
  link: string;
  title: string;
}

export const LegalSection: React.FC = () => {
  const { t } = useTranslation();

  const countries = [
    {
      documents: [
        {
          external: false,
          link: AppUrlEnum.ArWithdrawalCancellation,
          title: t('legal.ar.withdrawalCancellation'),
        },
        {
          external: false,
          link: AppUrlEnum.ArFees,
          title: t('legal.ar.fees'),
        },
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/ar/documents/privacy-policy.pdf',
          title: t('common.policy'),
        },
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/ar/documents/terms-conditions.pdf',
          title: t('common.terms'),
        },
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/ar/documents/cartel-atencion-al-cliente.pdf',
          title: t('legal.ar.financialUserSupportOfficers'),
        },
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/ar/documents/cartel-contacto-bcra-cartel-baja-servicios.pdf',
          title: t('legal.ar.relevantInfoForFinancialUsers'),
        },
      ] as LegalDocument[],
      flag: '/images/flags/argentina.svg',
      name: 'Argentina',
    },

    {
      documents: [
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/br/documents/privacy-policy.pdf',
          title: t('common.policy'),
        },
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/br/documents/personal-data.pdf',
          title: t('common.personalData'),
        },
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/br/documents/terms-conditions.pdf',
          title: t('common.terms'),
        },
      ],
      flag: '/images/flags/brazil.svg',
      name: 'Brazil',
    },
    {
      documents: [
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/co/documents/privacy-policy.pdf',
          title: t('common.policy'),
        },
      ],
      flag: '/images/flags/colombia.svg',
      name: 'Colombia',
    },
    {
      documents: [
        {
          link: 'https://rixsusassets.s3.us-east-2.amazonaws.com/mx/documents/privacy-policy.pdf',
          title: t('common.policy'),
        },
      ],
      flag: '/images/flags/mexico.svg',
      name: 'Mexico',
    },
  ];

  return (
    <section
      className="bg-no-hidden-fees bg-no-repeat bg-cover py-16 px-4 md:px-10 lg:px-20
      w-full max-w-[1204px]  overflow-hidden
      lg:w-[1204px]"
      id="legal"
    >
      <div className="text-center mb-12 max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 pb-10">
          {t('legal.title')}
        </h2>
        <p className="text-gray-600 mt-4 pb-10">{t('legal.subTitle')}</p>
      </div>

      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {countries.map((country) => (
          <Card
            key={country.name}
            title={
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                <img
                  src={country.flag}
                  alt={country.name}
                  className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover"
                />
                {country.name}
              </div>
            }
            className="rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <ul className="space-y-2">
              {country.documents.map((document) => (
                <li key={document.title}>
                  {document.external === false ? (
                    <Link
                      to={document.link}
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <FileTextOutlined /> {document.title}
                    </Link>
                  ) : (
                    <a
                      href={document.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <FileTextOutlined /> {document.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </section>
  );
};
